(() => {
  if (window.KrProductControls) return;
  const instances = new WeakMap();
  const initialize = root => {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const listenerOptions = { signal: controller.signal };
    const listen = (target, event, fn, options = {}) => target?.addEventListener(event, fn, { ...listenerOptions, ...options });
    const track = root.querySelector('[data-kr-track]');
    const slides = [...root.querySelectorAll('[data-kr-slide]')];
    const thumbs = [...root.querySelectorAll('[data-kr-thumb]')];
    const variantSelect = root.querySelector('[data-kr-variant]');
    const form = root.querySelector('.kr-pdp__form');
    const quantity = root.querySelector('[data-kr-quantity]');
    const addButton = root.querySelector('[data-kr-add]');
    const optionButtons = [...root.querySelectorAll('[data-kr-option]')];
    const optionGroups = root.querySelector('[data-kr-options]');
    const sticky = root.querySelector('[data-kr-sticky]');
    const stickyButton = root.querySelector('[data-kr-sticky-add]');
    const stickyQuantity = root.querySelector('[data-kr-sticky-quantity]');
    const stickyOptions = [...root.querySelectorAll('[data-kr-sticky-option]')];
    const legacySwatches = [...root.querySelectorAll('[data-kr-swatch]:not([data-kr-option])')];
    if (!quantity || !addButton || !track) return;
    let variants;
    try {
      variants = variantSelect ? [...variantSelect.options].map(element => ({ element, values: JSON.parse(element.dataset.options || '[]') })) : [];
    } catch (_) { return; }
    const normalize = value => String(value ?? '').trim().toLowerCase();
    const currentVariant = () => variants.find(entry => entry.element.value === variantSelect?.value);
    const pending = () => root.dataset.krPurchasePending === 'true';
    let index = 0;
    let galleryFrame = 0;
    let stickyFrame = 0;
    const paintGallery = next => {
      index = Math.max(0, Math.min(slides.length - 1, next));
      thumbs.forEach((thumb, position) => {
        if (position === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      const counter = root.querySelector('[data-kr-count]');
      if (counter) counter.textContent = String(index + 1);
    };
    const showSlide = (next, smooth = true) => {
      if (!slides.length || !Number.isFinite(next)) return;
      paintGallery(next);
      track.scrollTo({ left: slides[index].offsetLeft - slides[0].offsetLeft, behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
    };
    const normalizeQuantity = source => {
      const minimum = Math.max(1, Number.parseInt(quantity.min, 10) || 1);
      const step = Math.max(1, Number.parseInt(quantity.step, 10) || 1);
      const maximum = Number.parseInt(quantity.max, 10);
      const entered = Number.parseInt(source?.value ?? quantity.value, 10);
      let value = Number.isFinite(entered) ? Math.max(minimum, entered) : minimum;
      if (Number.isFinite(maximum)) value = Math.min(maximum, value);
      value = minimum + Math.floor(Math.max(0, value - minimum) / step) * step;
      quantity.value = String(value);
      if (stickyQuantity) {
        stickyQuantity.value = quantity.value;
        stickyQuantity.min = quantity.min;
        stickyQuantity.step = quantity.step;
        if (quantity.max) stickyQuantity.max = quantity.max;
        else stickyQuantity.removeAttribute('max');
      }
      root.querySelectorAll('[data-kr-minus], [data-kr-sticky-minus]').forEach(button => { button.disabled = pending() || value <= minimum; });
      root.querySelectorAll('[data-kr-plus], [data-kr-sticky-plus]').forEach(button => { button.disabled = pending() || (Number.isFinite(maximum) && value + step > maximum); });
    };
    const findChoice = (position, value) => {
      const current = currentVariant();
      if (!current) return undefined;
      const candidates = variants.filter(entry => normalize(entry.values[position]) === normalize(value));
      const exact = candidates.find(entry => entry.values.length === current.values.length && entry.values.every((item, i) => i === position || normalize(item) === normalize(current.values[i])));
      if (exact?.element.dataset.available === 'true') return exact;
      const available = candidates.filter(entry => entry.element.dataset.available === 'true');
      const score = entry => entry.values.reduce((total, item, i) => total + Number(i !== position && normalize(item) === normalize(current.values[i])), 0);
      return available.reduce((best, entry) => !best || score(entry) > score(best) ? entry : best, undefined) || exact;
    };
    const syncChoices = selected => {
      optionButtons.forEach(button => {
        const position = Number(button.dataset.optionPosition);
        const target = findChoice(position, button.dataset.optionValue);
        button.disabled = pending() || !target || target.element.dataset.available !== 'true';
        button.setAttribute('aria-pressed', String(normalize(selected?.values[position]) === normalize(button.dataset.optionValue)));
      });
      root.querySelectorAll('[data-kr-option-label]').forEach(label => { label.textContent = selected?.values[Number(label.dataset.krOptionLabel)] || ''; });
      stickyOptions.forEach(select => {
        const position = Number(select.dataset.optionPosition);
        select.value = selected?.values[position] || '';
        select.disabled = pending();
        [...select.options].forEach(option => {
          const target = findChoice(position, option.value);
          option.disabled = !target || target.element.dataset.available !== 'true';
        });
      });
    };
    const setVariant = (moveImage = false, updateUrl = false) => {
      if (!variantSelect) return;
      const selected = currentVariant();
      const available = Boolean(selected && selected.element.dataset.available === 'true');
      if (!pending()) {
        addButton.disabled = !available || root.dataset.purchaseEnabled === 'false';
        addButton.textContent = root.dataset.purchaseEnabled === 'false' ? addButton.dataset.holdLabel : (available ? addButton.dataset.label : addButton.dataset.soldLabel);
        if (stickyButton) { stickyButton.disabled = !available; stickyButton.textContent = addButton.textContent; }
      }
      root.querySelectorAll('[data-kr-price], [data-kr-original-price]').forEach(element => { element.textContent = selected?.element.dataset.price || ''; });
      root.querySelectorAll('[data-kr-offer-price]').forEach(element => { element.textContent = selected?.element.dataset.offerPrice || selected?.element.dataset.price || ''; });
      const availability = root.querySelector('[data-kr-availability]');
      if (availability) availability.textContent = available ? availability.dataset.availableLabel : availability.dataset.unavailableLabel;
      if (selected) {
        quantity.min = selected.element.dataset.quantityMin || '1';
        quantity.step = selected.element.dataset.quantityStep || '1';
        if (selected.element.dataset.quantityMax) quantity.max = selected.element.dataset.quantityMax;
        else quantity.removeAttribute('max');
        normalizeQuantity();
        const stickyImage = root.querySelector('[data-kr-sticky-image]');
        if (stickyImage && selected.element.dataset.image) {
          stickyImage.removeAttribute('srcset');
          stickyImage.src = selected.element.dataset.image;
        }
      }
      syncChoices(selected);
      if (moveImage && selected) {
        const mediaIndex = slides.findIndex(slide => slide.dataset.mediaId === selected.element.dataset.mediaId);
        if (mediaIndex >= 0) showSlide(mediaIndex);
      }
      if (updateUrl && selected && root.dataset.nativeProduct === 'true') {
        const url = new URL(location.href);
        url.searchParams.set('variant', selected.element.value);
        url.searchParams.delete('color');
        history.replaceState(history.state, '', url);
      }
    };
    const choose = (position, value) => {
      if (pending()) return;
      const next = findChoice(position, value);
      if (!next || next.element.dataset.available !== 'true') { syncChoices(currentVariant()); return; }
      variantSelect.value = next.element.value;
      variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    };
    const syncSticky = () => {
      stickyFrame = 0;
      if (!sticky || !root.isConnected) return;
      const desktop = innerWidth >= 1000;
      const header = document.querySelector('.kr-header');
      const headerBottom = header ? Math.max(0, Math.min(innerHeight, header.getBoundingClientRect().bottom)) : 0;
      sticky.style.setProperty('--kr-quick-top', `${Math.round(headerBottom)}px`);
      const purchase = root.querySelector('[data-kr-purchase]');
      const footer = document.querySelector('footer');
      const footerVisible = footer && footer.getBoundingClientRect().top < innerHeight;
      const active = document.activeElement;
      const typingInField = !desktop && active && !sticky.contains(active) && /^(INPUT|TEXTAREA)$/.test(active.tagName);
      const purchaseRect = purchase?.getBoundingClientRect();
      const addRect = addButton.getBoundingClientRect();
      const mobileStarted = track.getBoundingClientRect().top <= headerBottom - 100;
      const mainButtonVisible = addRect.top < innerHeight - 116 && addRect.bottom > headerBottom;
      const showForPosition = desktop
        ? Boolean(purchaseRect && purchaseRect.bottom <= headerBottom)
        : mobileStarted && !mainButtonVisible;
      sticky.hidden = !form || root.dataset.live !== 'true' || !purchase || !showForPosition || Boolean(footerVisible) || typingInField;
    };
    const scheduleSticky = () => { if (!stickyFrame) stickyFrame = requestAnimationFrame(syncSticky); };
    thumbs.forEach(thumb => listen(thumb, 'click', () => showSlide(Number(thumb.dataset.krThumb))));
    listen(root.querySelector('[data-kr-prev]'), 'click', () => showSlide((index - 1 + slides.length) % slides.length));
    listen(root.querySelector('[data-kr-next]'), 'click', () => showSlide((index + 1) % slides.length));
    listen(track, 'keydown', event => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      showSlide(index + (event.key === 'ArrowRight' ? 1 : -1));
    });
    listen(track, 'scroll', () => {
      if (galleryFrame || !slides.length) return;
      galleryFrame = requestAnimationFrame(() => {
        galleryFrame = 0;
        paintGallery(slides.reduce((nearest, slide, position) => Math.abs(slide.offsetLeft - slides[0].offsetLeft - track.scrollLeft) < Math.abs(slides[nearest].offsetLeft - slides[0].offsetLeft - track.scrollLeft) ? position : nearest, 0));
      });
    }, { passive: true });
    optionButtons.forEach(button => listen(button, 'click', () => choose(Number(button.dataset.optionPosition), button.dataset.optionValue)));
    stickyOptions.forEach(select => listen(select, 'change', () => choose(Number(select.dataset.optionPosition), select.value)));
    legacySwatches.forEach(button => listen(button, 'click', () => {
      legacySwatches.forEach(other => other.setAttribute('aria-pressed', String(other === button)));
      root.querySelectorAll('[data-kr-color-name], [data-kr-sticky-color]').forEach(label => { label.textContent = button.dataset.color || ''; });
      showSlide(Number(button.dataset.galleryIndex));
    }));
    listen(variantSelect, 'change', () => { if (!pending()) setVariant(true, true); });
    root.querySelectorAll('[data-kr-minus], [data-kr-sticky-minus], [data-kr-plus], [data-kr-sticky-plus]').forEach(button => listen(button, 'click', () => {
      if (pending()) return;
      const decrease = button.hasAttribute('data-kr-minus') || button.hasAttribute('data-kr-sticky-minus');
      quantity.value = String(Number(quantity.value) + (decrease ? -1 : 1) * (Number(quantity.step) || 1));
      normalizeQuantity();
    }));
    [quantity, stickyQuantity].filter(Boolean).forEach(input => {
      listen(input, 'change', () => { if (!pending()) normalizeQuantity(input); });
      listen(input, 'blur', () => { if (!pending()) normalizeQuantity(input); });
    });
    listen(form, 'submit', event => {
      normalizeQuantity();
      if (root.dataset.live !== 'true' || root.dataset.purchaseEnabled === 'false' || pending() || addButton.disabled || !variantSelect?.value) event.preventDefault();
    });
    listen(stickyButton, 'click', () => {
      if (stickyQuantity) normalizeQuantity(stickyQuantity);
      if (form && !stickyButton.disabled && !pending()) form.requestSubmit(addButton);
    });
    listen(root, 'kr:purchase-pending', () => { if (!pending()) setVariant(); });
    listen(window, 'scroll', scheduleSticky, { passive: true });
    listen(document, 'scroll', scheduleSticky, { passive: true, capture: true });
    listen(window, 'resize', scheduleSticky, { passive: true });
    listen(document, 'focusin', scheduleSticky);
    listen(document, 'focusout', scheduleSticky);
    const resizeObserver = new ResizeObserver(scheduleSticky);
    resizeObserver.observe(root);
    const header = document.querySelector('.kr-header');
    if (header) resizeObserver.observe(header);
    const destroy = () => { controller.abort(); resizeObserver.disconnect(); cancelAnimationFrame(galleryFrame); cancelAnimationFrame(stickyFrame); instances.delete(root); };
    instances.set(root, { destroy });
    normalizeQuantity();
    const parameters = new URLSearchParams(location.search);
    const requestedVariant = parameters.get('variant');
    const requestedColor = parameters.get('color');
    const hasVariant = requestedVariant && variants.some(entry => entry.element.value === requestedVariant);
    if (variantSelect && hasVariant) variantSelect.value = requestedVariant;
    if (variantSelect && requestedColor && !hasVariant) {
      const colorPosition = Number(root.dataset.colorPosition) - 1;
      const choice = findChoice(colorPosition, requestedColor);
      if (choice?.element.dataset.available === 'true') variantSelect.value = choice.element.value;
    }
    setVariant(Boolean(hasVariant || requestedColor));
    if (variantSelect && (optionButtons.length || root.dataset.onlyDefaultVariant === 'true')) {
      const fallback = root.querySelector('.kr-pdp__variant-field');
      if (fallback) fallback.hidden = true;
      if (optionGroups) optionGroups.hidden = false;
    }
    syncSticky();
  };
  const initializeAll = scope => scope.querySelectorAll('[data-kr-pdp]').forEach(initialize);
  window.KrProductControls = { initialize };
  initializeAll(document);
  document.addEventListener('shopify:section:load', event => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-kr-pdp]').forEach(root => instances.get(root)?.destroy()));
})();


